import {
  Box,
  Card,
  Divider,
  InputAdornment,
  TextField,
  Typography,
} from '@material-ui/core';
import React, {useEffect, useState} from 'react';
import {
  IFarmInputInvoice,
  IFarmInputPurchaseOrder,
  IGenericType,
} from 'constants/interfaces';
import {Skeleton} from '@material-ui/lab';
import moment from 'moment';
import {_NumberWithSpaces} from 'constants/index';
import {Link} from 'react-router-dom';
import {purchaseOrderTotal} from 'screens/Dashboard/PurchaseOrders/PurchaseOrderTableSetup';
import IconButton from '@material-ui/core/IconButton';
import SaveIcon from '@material-ui/icons/Save';
import EditIcon from '@material-ui/icons/Edit';
import {apiSavePurchaseOrder} from 'constants/Api';

export function PurchaseOrderDetailCard(props: {
  purchaseOrder?: IFarmInputPurchaseOrder;
  invoiceExclVat?: IFarmInputInvoice;
  purchaseOrderStatuses: IGenericType[];
  isLoading?: boolean;
}) {
  const [isEditingTrackingReference, setIsEditingTrackingReference] =
    useState(false);
  const [trackingReference, setTrackingReference] = useState<string>();

  useEffect(() => {
    setTrackingReference(props.purchaseOrder?.trackingReference);
  }, [props.purchaseOrder]);

  const handleUpdateTrackingReference = () => {
    if (!props.purchaseOrder) return;
    apiSavePurchaseOrder({
      id: props.purchaseOrder.id,
      trackingReference: trackingReference,
    })
      .catch((error) => {
        alert('Error updating tracking reference. Please try again later');
        console.error(error);
      })
      .finally(() => setIsEditingTrackingReference(false));
  };

  if (props.isLoading)
    return <Skeleton variant="rect" height={200} style={{borderRadius: 8}} />;

  const currStatus = () => {
    if (!props.purchaseOrder) return undefined;
    const statusUpdates = props.purchaseOrder.statusUpdates.sort(
      (a, b) => parseInt(a.date) - parseInt(b.date),
    );
    if (statusUpdates.length === 0)
      return props.purchaseOrderStatuses.sort(
        (a, b) => parseInt(a.id) - parseInt(b.id),
      )[0];
    return statusUpdates[statusUpdates.length - 1].status;
  };

  if (!props.purchaseOrder) return null;
  const {purchaseOrder} = props;

  const formatDate = (date: Date): string => {
    return moment(date).format('YYYY-MM-DD');
  };
  const purchaseOrderDate = (purchaseOrder: IFarmInputPurchaseOrder) => {
    return formatDate(new Date(parseInt(purchaseOrder.createdDate)));
  };
  const findStatusName = (id: number) => {
    return (
      props.purchaseOrderStatuses.find((status) => status.id === id)?.name || ''
    );
  };
  const productTotal = () => {
    if (!props.invoiceExclVat) return '';
    return props.invoiceExclVat.orders?.reduce(
      (total, order) => total + order.price * order.quantity,
      0,
    );
  };
  return (
    <Card className="farmer-detail-card" variant="outlined">
      <Box className="row">
        <Box className="title-container">
          <Typography variant="subtitle2">Date</Typography>
        </Box>
        <Box className="value-container">
          <Typography variant="body2" color="textSecondary">
            {purchaseOrderDate(purchaseOrder)}
          </Typography>
        </Box>
      </Box>
      <Divider />
      <Box className="row">
        <Box className="title-container">
          <Typography variant="subtitle2">Invoice</Typography>
        </Box>
        <Box className="value-container">
          <Link
            to={`/orders/${purchaseOrder?.invoice.id}`}
            className="list-item"
          >
            <Typography variant="body2" color="textSecondary">
              {purchaseOrder?.invoice.invoiceNumber}
            </Typography>
          </Link>
        </Box>
      </Box>
      <Divider />
      <Box className="row">
        <Box className="title-container">
          <Typography variant="subtitle2">Supplier</Typography>
        </Box>
        <Box className="value-container">
          <Typography variant="body2" color="textSecondary">
            {purchaseOrder?.mainSupplier?.name ??
              purchaseOrder.invoice.depot?.mainSupplier?.name}
          </Typography>
        </Box>
      </Box>
      <Divider />
      <Box className="row">
        <Box className="title-container">
          <Typography variant="subtitle2">Depot</Typography>
        </Box>
        <Box className="value-container">
          <Typography variant="body2" color="textSecondary">
            {purchaseOrder?.invoice.depot.name}
          </Typography>
        </Box>
      </Box>
      <Divider />
      <Box className="row">
        <Box className="title-container">
          <Typography variant="subtitle2">Status</Typography>
        </Box>
        <Box className="value-container">
          <Typography variant="body2" color="textSecondary">
            {findStatusName(currStatus()?.id || -1)}
          </Typography>
        </Box>
      </Box>
      <Divider />
      <Box className="row">
        <Box className="title-container">
          <Typography variant="subtitle2">Num Items</Typography>
        </Box>
        <Box className="value-container">
          <Typography variant="body2" color="textSecondary">
            {purchaseOrder?.orders.length}
          </Typography>
        </Box>
      </Box>
      <Divider />
      <Box className="row">
        <Box className="title-container">
          <Typography variant="subtitle2">Products</Typography>
        </Box>
        <Box className="value-container">
          <Typography variant="body2" color="textSecondary">
            R {_NumberWithSpaces(productTotal() ?? 0)}
          </Typography>
        </Box>
      </Box>
      <Divider />
      <Box className="row">
        <Box className="title-container">
          <Typography variant="subtitle2">Logistics</Typography>
        </Box>
        <Box className="value-container">
          <Typography variant="body2" color="textSecondary">
            R {_NumberWithSpaces(purchaseOrder?.invoice?.logisticsCost || 0)}
          </Typography>
        </Box>
      </Box>
      <Divider />
      <Box className="row">
        <Box className="title-container">
          <Typography variant="subtitle2">Total</Typography>
        </Box>
        <Box className="value-container">
          <Typography variant="body2" color="textSecondary">
            R {_NumberWithSpaces(purchaseOrderTotal(purchaseOrder) ?? 0)}
          </Typography>
        </Box>
      </Box>
      <Divider />
      <Box className="row">
        <Box className="title-container">
          <Typography variant="subtitle2">Tracking Reference</Typography>
        </Box>
        <Box className="value-container">
          {!isEditingTrackingReference && (
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
            >
              <Typography variant="body2" color="textSecondary">
                {trackingReference}
              </Typography>
              <IconButton
                edge="end"
                color="inherit"
                onClick={() => setIsEditingTrackingReference(true)}
                size="small"
              >
                <EditIcon fontSize="inherit" />
              </IconButton>
            </Box>
          )}
          {isEditingTrackingReference && (
            <TextField
              className="input"
              variant="outlined"
              fullWidth
              value={trackingReference}
              onChange={(event) => setTrackingReference(event.target.value)}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      color="inherit"
                      onClick={handleUpdateTrackingReference}
                      size="small"
                    >
                      <SaveIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Box>
      </Box>
      <Divider />
      <Box className="row">
        <Box className="title-container">
          <Typography variant="subtitle2">Expected Payment Date</Typography>
        </Box>
        <Box className="value-container">
          <Typography variant="body2" color="textSecondary">
            {purchaseOrder?.expectedPaymentDate
              ? formatDate(
                  new Date(parseInt(purchaseOrder?.expectedPaymentDate)),
                )
              : ''}
          </Typography>
        </Box>
      </Box>
      <Divider />
    </Card>
  );
}
